import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib/cn';
import { Avatar, Badge, Card } from '@/shared/ui';
import type { GroupTableRow } from '../model/drawView';

export interface GroupStandingsCardProps {
  title: string;
  rows: readonly GroupTableRow[];
  /** Ile najlepszych miejsc awansuje — steruje wyróżnieniem i legendą. */
  advancing?: number | null;
  /** Ile dodatkowych miejsc z dalszych pozycji ma awans — steruje legendą. */
  extraQualifiers?: number | null;
  /** Etykieta postępu meczów w prawym górnym rogu, np. „3 / 6 meczów”. */
  matchesLabel?: string;
}

/**
 * Tabela grupy: miejsce, zawodnik, mecze, W/P, bilans gemów i punkty.
 * Miejsca awansujące do fazy finałowej są podświetlone kolorem marki;
 * drużyny zajmujące w tej chwili dodatkowe miejsca awansu z dalszych pozycji
 * (`row.extraQualifier`) — kolorem sukcesu. Legenda pod tabelą tłumaczy oba.
 */
export function GroupStandingsCard({
  title,
  rows,
  advancing,
  extraQualifiers,
  matchesLabel,
}: GroupStandingsCardProps) {
  const { t } = useTranslation('tournament');

  return (
    <Card padding="md" className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-bold tracking-[0.04em] text-fg-strong uppercase">{title}</h3>
        {matchesLabel && (
          <Badge tone="neutral" size="sm">
            {matchesLabel}
          </Badge>
        )}
      </div>

      <div className="overflow-x-auto">
        {/* table-fixed: stałe kolumny liczbowe, nazwisko przycina się zamiast rozpychać tabelę */}
        <table className="w-full min-w-[22rem] table-fixed border-collapse text-sm">
          <thead>
            <tr className="text-[0.6875rem] font-bold tracking-[0.08em] text-fg-muted uppercase">
              <th className="w-8 py-1.5 pr-2 text-right font-bold">{t('standings.position')}</th>
              <th className="py-1.5 pr-2 text-left font-bold">{t('standings.player')}</th>
              <th className="w-8 py-1.5 text-center font-bold">{t('standings.played')}</th>
              <th className="w-14 py-1.5 text-center font-bold">{t('standings.winsLosses')}</th>
              <th className="w-14 py-1.5 text-center font-bold">{t('standings.games')}</th>
              <th className="w-10 py-1.5 text-right font-bold">{t('standings.points')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              // Awans oznaczamy tylko kolorem miejsca i pogrubieniem — bez tła wiersza.
              <tr key={row.ids.join('+')} className="border-t border-border">
                <td
                  className={cn(
                    'py-2 pr-2 text-right text-sm tabular-nums',
                    row.advances
                      ? 'font-bold text-brand-text'
                      : row.extraQualifier
                        ? 'font-bold text-success-600'
                        : 'text-fg-subtle',
                  )}
                >
                  {row.rank ?? index + 1}
                </td>
                <td className="py-2 pr-2">
                  <span className="flex min-w-0 items-center gap-2">
                    <Avatar name={row.label} size="xs" colorful />
                    <span
                      className={cn(
                        'truncate text-sm',
                        row.advances || row.extraQualifier
                          ? 'font-semibold text-fg-strong'
                          : 'text-fg',
                      )}
                    >
                      {row.label}
                    </span>
                    {row.extraQualifier && (
                      <Badge tone="success" size="sm" className="shrink-0">
                        {t('standings.extraBadge')}
                      </Badge>
                    )}
                  </span>
                </td>
                <td className="py-2 text-center text-fg-muted tabular-nums">{row.played}</td>
                <td className="py-2 text-center text-fg tabular-nums">
                  <span className="font-semibold text-fg-strong">{row.wins}</span>
                  <span className="text-fg-subtle">–{row.losses}</span>
                </td>
                <td
                  className={cn(
                    'py-2 text-center tabular-nums',
                    row.gamesBalance > 0
                      ? 'text-success-600'
                      : row.gamesBalance < 0
                        ? 'text-danger-600'
                        : 'text-fg-muted',
                  )}
                >
                  {row.gamesBalance > 0 ? `+${row.gamesBalance}` : row.gamesBalance}
                </td>
                <td className="py-2 text-right font-extrabold text-fg-strong tabular-nums">
                  {row.points}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {advancing != null && advancing > 0 && (
        <p className="flex items-center gap-1.5 text-xs text-fg-muted">
          <span aria-hidden="true" className="inline-block size-2 rounded-full bg-brand" />
          {t('standings.advanceLegend', { count: advancing })}
        </p>
      )}

      {extraQualifiers != null && extraQualifiers > 0 && advancing != null && advancing > 0 && (
        <p className="flex items-center gap-1.5 text-xs text-fg-muted">
          <span aria-hidden="true" className="inline-block size-2 rounded-full bg-success-600" />
          {t('standings.extraLegend', { count: extraQualifiers, place: advancing + 1 })}
        </p>
      )}
    </Card>
  );
}
